import React, { useCallback, useMemo, useState } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    Alert,
    Divider,
    Switch,
    FormControlLabel,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { useUsuarios } from "../hooks";
import { getUsuariosColumns } from "../helpers/columns";
import {
    UsuariosMobileList,
    UsuariosDataGrid,
    ModalUsuario,
    ModalDarDeBaja,
    ModalRestaurarUsuario,
} from "../components";

/**
 * Página de listado de usuarios (DataGrid en escritorio, tarjetas en móvil).
 * Orquestador: useUsuarios aporta los datos, el filtro incluirBajas y las
 * operaciones CRUD; helpers/columns.js define las columnas de escritorio y
 * UsuariosMobileList la vista móvil, con la misma firma de callbacks para no
 * necesitar adaptadores entre ambas.
 *
 * Los tres modales (ModalUsuario, ModalDarDeBaja, ModalRestaurarUsuario) son
 * "inteligentes" (TASK-009/TASK-010): gestionan su propio loading local y sus
 * propios toasts de éxito/error. Esta página no reimplementa nada de eso, solo
 * decide qué modal está abierto y sobre qué usuario, y les pasa las funciones
 * del hook (crearUsuario/actualizarUsuario/darDeBaja/restaurarUsuario).
 *
 * Control de acceso: el rol ADMIN requerido para esta página se resuelve en
 * src/routes.js (roles: [Roles.ADMIN.role]), NO aquí (TASK-013). Envolver
 * también este componente en RequierePermisos duplicaría la protección.
 */
const ListaUsuarios = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const {
        usuarios,
        loading,
        error,
        incluirBajas,
        setIncluirBajas,
        crearUsuario,
        actualizarUsuario,
        darDeBaja,
        restaurarUsuario,
    } = useUsuarios();

    // DECISION (idOperacionEnCurso, TASK-011): con los 3 modales gestionando
    // su propio loading (TASK-009/TASK-010), este estado ya NO representa una
    // petición HTTP en curso, sino "qué fila tiene un modal propio abierto"
    // (edición, baja o restaurar). Se fija al abrir cualquiera de esos 3
    // modales y se limpia al cerrarlos (éxito o cancelación); columns.js y
    // UsuariosMobileList lo usan para deshabilitar las acciones de esa fila
    // mientras dura. "Nuevo usuario" no fija ningún id (no hay fila asociada).
    const [idOperacionEnCurso, setIdOperacionEnCurso] = useState(null);
    const [modalFormulario, setModalFormulario] = useState({ open: false, usuario: null });
    const [usuarioParaDarBaja, setUsuarioParaDarBaja] = useState(null);
    const [usuarioParaRestaurar, setUsuarioParaRestaurar] = useState(null);

    const hayOperacionEnCurso = idOperacionEnCurso !== null;

    const handleNuevoUsuario = useCallback(() => {
        setModalFormulario({ open: true, usuario: null });
    }, []);

    const handleEditar = useCallback((usuario) => {
        setModalFormulario({ open: true, usuario });
        setIdOperacionEnCurso(usuario.id);
    }, []);

    const handleCerrarFormulario = useCallback(() => {
        setModalFormulario({ open: false, usuario: null });
        setIdOperacionEnCurso(null);
    }, []);

    const handleDarBaja = useCallback((usuario) => {
        setUsuarioParaDarBaja(usuario);
        setIdOperacionEnCurso(usuario.id);
    }, []);

    const handleCerrarDarBaja = useCallback(() => {
        setUsuarioParaDarBaja(null);
        setIdOperacionEnCurso(null);
    }, []);

    const handleRestaurar = useCallback((usuario) => {
        setUsuarioParaRestaurar(usuario);
        setIdOperacionEnCurso(usuario.id);
    }, []);

    const handleCerrarRestaurar = useCallback(() => {
        setUsuarioParaRestaurar(null);
        setIdOperacionEnCurso(null);
    }, []);

    const handleToggleIncluirBajas = useCallback(
        (event) => setIncluirBajas(event.target.checked),
        [setIncluirBajas]
    );

    const columns = useMemo(
        () => getUsuariosColumns({ onEditar: handleEditar, onDarBaja: handleDarBaja, onRestaurar: handleRestaurar, idOperacionEnCurso }),
        [handleEditar, handleDarBaja, handleRestaurar, idOperacionEnCurso]
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            gap: 2,
                            mb: 0.5,
                        }}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <PeopleAltIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Usuarios
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Consulta y gestiona los usuarios del sistema, sus roles y su estado
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNuevoUsuario}
                            disabled={hayOperacionEnCurso}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Nuevo usuario
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <FormControlLabel
                        control={<Switch checked={incluirBajas} onChange={handleToggleIncluirBajas} />}
                        label="Mostrar usuarios dados de baja"
                    />
                    {/* Matiz de producto (TASK-011): con el filtro desactivado, dar de baja a un
                        usuario lo hace desaparecer de la lista al recargar. El toast de éxito del
                        modal es transitorio (5s); este texto queda siempre visible junto al switch
                        para dejar claro que no se ha borrado, solo evitar la confusión "lo borré". */}
                    <Typography variant="caption" sx={{ display: "block", color: "#6c757d", mt: 0.5 }}>
                        Un usuario dado de baja no se elimina: conserva su historial y puedes
                        restaurarlo activando este filtro.
                    </Typography>
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!isMobile ? (
                        <UsuariosDataGrid rows={usuarios} columns={columns} loading={loading} />
                    ) : (
                        <UsuariosMobileList
                            usuarios={usuarios}
                            loading={loading}
                            incluirBajas={incluirBajas}
                            onEditar={handleEditar}
                            onDarBaja={handleDarBaja}
                            onRestaurar={handleRestaurar}
                            idOperacionEnCurso={idOperacionEnCurso}
                        />
                    )}
                </Box>
            </Card>

            <ModalUsuario
                show={modalFormulario.open}
                usuario={modalFormulario.usuario}
                onHide={handleCerrarFormulario}
                crearUsuario={crearUsuario}
                actualizarUsuario={actualizarUsuario}
                restaurarUsuario={restaurarUsuario}
            />

            <ModalDarDeBaja
                show={Boolean(usuarioParaDarBaja)}
                usuario={usuarioParaDarBaja}
                onHide={handleCerrarDarBaja}
                darDeBaja={darDeBaja}
            />

            <ModalRestaurarUsuario
                show={Boolean(usuarioParaRestaurar)}
                usuario={usuarioParaRestaurar}
                onHide={handleCerrarRestaurar}
                restaurarUsuario={restaurarUsuario}
            />
        </Box>
    );
};

export default ListaUsuarios;
